import { useEffect, useState } from "react";
import { Filter } from "lucide-react";
import { db } from "@/lib/db";
import type { SavedView, Document } from "@/types";

export default function SmartFolders() {
  const [views, setViews] = useState<SavedView[]>([]);
  const [newName, setNewName] = useState("");
  const [selectedView, setSelectedView] = useState<string | null>(null);
  const [docs, setDocs] = useState<Document[]>([]);

  const loadViews = () => db.savedViews.toArray().then(setViews);

  useEffect(() => { loadViews(); }, []);

  useEffect(() => {
    if (!selectedView) { setDocs([]); return; }
    db.savedViews.get(selectedView).then(async (view) => {
      if (!view) return;
      let query = db.documents.toCollection();
      const f = view.filters;
      if (f.mimeType) query = query.filter((d) => d.mimeType.includes(f.mimeType as string));
      if (f.extension) query = query.filter((d) => d.extension === f.extension);
      if (f.minSize) query = query.filter((d) => d.sizeBytes >= (f.minSize as number));
      setDocs(await query.toArray());
    });
  }, [selectedView]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    await db.savedViews.add({
      id: crypto.randomUUID(),
      name: newName.trim(),
      filters: { mimeType: "pdf" },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
    setNewName("");
    loadViews();
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this saved view?")) return;
    await db.savedViews.delete(id);
    loadViews();
  };

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-border-default bg-surface-0 p-5 shadow-sm dark:border-dark-border-default dark:bg-dark-surface-1">
        <h3 className="text-sm font-semibold text-text-primary dark:text-dark-text-primary">New Saved View</h3>
        <div className="mt-3 flex gap-2">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="View name (e.g., All PDFs)"
            className="flex-1 rounded-lg border border-border-strong bg-surface-0 px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-100 dark:border-dark-border-strong dark:bg-dark-surface-2 dark:text-dark-text-primary"
          />
          <button onClick={handleCreate} className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-accent-600">
            Create
          </button>
        </div>
      </div>

      {views.length === 0 ? (
        <div className="rounded-xl border border-border-default bg-surface-0 p-8 text-center shadow-sm dark:border-dark-border-default dark:bg-dark-surface-1">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-surface-2 dark:bg-dark-surface-2 mb-3">
            <Filter size={24} className="text-text-muted" />
          </div>
          <p className="text-sm font-medium text-text-secondary dark:text-dark-text-secondary">No saved views yet</p>
          <p className="mt-1 text-xs text-text-muted">Create a saved view to quickly filter your documents</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {views.map((view) => (
            <div
              key={view.id}
              onClick={() => setSelectedView(view.id === selectedView ? null : view.id)}
              className={`cursor-pointer rounded-xl border p-5 shadow-sm transition-shadow hover:shadow-md ${
                selectedView === view.id ? "border-accent bg-accent/5 dark:border-accent" : "border-border-default bg-surface-0 dark:border-dark-border-default dark:bg-dark-surface-1"
              }`}
            >
              <h4 className="font-medium text-text-primary dark:text-dark-text-primary">{view.name}</h4>
              <p className="mt-1 text-xs text-text-muted dark:text-dark-text-muted">{Object.keys(view.filters).length} filters</p>
              <button onClick={(e) => { e.stopPropagation(); handleDelete(view.id); }} className="mt-3 text-xs text-danger hover:underline">Delete</button>
            </div>
          ))}
        </div>
      )}

      {selectedView && docs.length > 0 && (
        <div className="rounded-xl border border-border-default bg-surface-0 p-5 shadow-sm dark:border-dark-border-default dark:bg-dark-surface-1">
          <h3 className="text-sm font-semibold text-text-primary dark:text-dark-text-primary">Matching documents ({docs.length})</h3>
          <div className="mt-3 space-y-2">
            {docs.map((d) => (
              <div key={d.id} className="text-sm text-text-secondary dark:text-dark-text-secondary">{d.canonicalName}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
